import React from "react";
import { Link } from "react-router-dom";
import { FactList, FicheHeader, FicheSection, Timeline } from "../../../common/Fiche";
import tools from "../../../../helpers/tools";
import { estAnonymise } from "../../../../helpers/allocataire";
import StatutPropositionBadge from "./StatutPropositionBadge";
import {
  COULEUR_FT,
  COULEUR_KO,
  COULEUR_NEUTRE,
  COULEUR_OK,
  Fait,
  formatDateHeure,
  LigneReponse,
} from "./blocsSanctionFT";
import "./detail-proposition.scss";

/**
 * La teinte d'une ligne du tableau, d'après ce qui compte : vert quand France
 * Travail a reçu notre décision (la chaîne est allée au bout), orange quand
 * quelque chose bloque ou que FT n'a pas pris l'événement en compte, rouge
 * pour un rejet avant EP. Null pour ce qui suit paisiblement son cours.
 */
export const tonLigne = (p) => {
  if (p.blocage) return "warn";
  if (p.decisionRemontee) return p.decisionRemontee.priseEnCompte ? "ok" : "warn";
  if (p.statut === "REJETEE") return "ko";
  return null;
};

const TITRE_EP = "Décision de l'EP";

/**
 * Ce que la CAF a fait de la proposition, résumé pour la carte « décision » :
 * titre de la carte, ton, libellé principal et mention (identique, modifiée,
 * aucune sanction…). Un rejet automatique n'est pas une décision d'EP : la
 * carte le dit dans son titre.
 */
export const decisionFinale = (p) => {
  const i = p.instructionEP;
  const d = i?.decisionFinale;

  if (d) {
    if (d.pourcentageReduction === 0 && d.dureeEnMois === 0) {
      return { titre: TITRE_EP, ton: "ko", label: d.label, mention: "L'EP n'a pas sanctionné" };
    }
    const identique =
      d.pourcentageReduction === p.propositionFT?.montant
      && d.dureeEnMois === p.propositionFT?.duree
      && (!p.propositionFT?.typeConsequenceLabel || d.label.startsWith(p.propositionFT.typeConsequenceLabel));
    return identique
      ? { titre: TITRE_EP, ton: "ok", label: d.label, mention: "Conforme à la proposition" }
      : { titre: TITRE_EP, ton: "warn", label: d.label, mention: "Modifiée par rapport à la proposition" };
  }
  if (p.statut === "REJETEE") {
    return {
      titre: "Rejet automatique ESPOAR",
      ton: "ko",
      label: p.motifRejetLabel || "Rejetée avant EP",
      mention: "Sans passage en EP",
    };
  }
  if (i) {
    return { titre: TITRE_EP, ton: "pending", label: "En attente de décision de l'EP", mention: null };
  }
  return { titre: TITRE_EP, ton: "pending", label: "Non inscrite en EP", mention: null };
};

/**
 * Les événements de France Travail et les nôtres dans une seule chronologie,
 * du plus récent au plus ancien. C'est ici que le détail du manquement devient
 * lisible : la saisie « recentralisation » y prend sa place entre la
 * proposition du conseiller et notre réponse.
 */
export const chronologie = (p) => {
  const items = [];

  (p.detailManquement?.evenements ?? []).forEach((e, k) => {
    items.push({
      id: `ft-${k}`,
      date: e.date,
      icon: "building",
      color: COULEUR_FT,
      variant: "ft",
      title: e.typeLabel,
      body: e.motifLabel ? <p className="mb-0 small text-muted">{e.motifLabel}</p> : null,
    });
  });

  if (p.dateReception) {
    items.push({
      id: "reception",
      date: p.dateReception,
      icon: "inbox",
      color: COULEUR_NEUTRE,
      title: "Proposition reçue par ESPOAR",
    });
  }

  if (p.decisionRemontee) {
    const ok = p.decisionRemontee.priseEnCompte;
    items.push({
      id: "remontee",
      date: p.decisionRemontee.dateRemontee,
      icon: ok ? "check" : "exclamation",
      color: ok ? COULEUR_OK : COULEUR_KO,
      title: `${p.decisionRemontee.label} — ${ok ? "reçue par France Travail" : "non prise en compte par France Travail"}`,
      body: <p className="mb-0 small text-muted">Code France Travail : {p.decisionRemontee.codeSortie}</p>,
    });
  }

  if (p.radiation) {
    items.push({
      id: "radiation",
      date: p.radiation.dateDemande,
      icon: p.radiation.effectuee ? "user-times" : "minus-circle",
      color: p.radiation.effectuee ? COULEUR_OK : COULEUR_NEUTRE,
      title: p.radiation.label,
      body: <p className="mb-0 small text-muted">Code France Travail : {p.radiation.codeSortie}</p>,
    });
  }

  if (p.blocage) {
    items.push({
      id: "blocage",
      date: p.blocage.depuis,
      icon: "exclamation-triangle",
      color: COULEUR_KO,
      title: `Dernière tentative en échec — ${p.blocage.etapeLabel}`,
      body: <p className="mb-0 small font-weight-bold">{p.blocage.raison}</p>,
    });
  }

  return items.sort((a, b) => new Date(b.date) - new Date(a.date));
};

/**
 * Fiche d'une proposition de sanction France Travail, ouverte dans le panneau
 * latéral : le tableau ne garde que de quoi repérer et trier, tout ce qui
 * explique un dossier vit ici.
 */
const DetailPropositionSanctionFT = ({ proposition: p, onRelancer, enCoursDeRelance = false }) => {
  const nomComplet = p.allocataire
    ? `${p.allocataire.nom || ""} ${p.allocataire.prenom || ""}`.trim()
    : null;
  const d = p.detailManquement;
  const i = p.instructionEP;
  const finale = decisionFinale(p);
  // L'API dit si une relance a un sens (dossier ouvert, EP finalisée quand il
  // s'agit de remonter la décision, réponse à la levée bloquée…) : la règle
  // vit chez elle, l'écran ne la redéfinit pas. Même règle que le bouton de
  // la ligne.
  const relancable = Boolean(p.relancable) && !estAnonymise(p.allocataire);

  const aUneReponse = p.decisionRemontee || p.radiation || p.detailLevee?.reponse || (p.statut === "REJETEE" && p.codeSortieRefus);

  return (
    <div className="detail-proposition fiche-page fiche-page--modal">
      <FicheHeader
        headingLevel="h2"
        eyebrow={`Proposition France Travail n° ${p.idConsequenceSanction} · ${p.codeActivite}`}
        title={
          p.allocataire && nomComplet ? (
            <Link
              to={tools.getRecipientLinkFromParams(p.allocataire.type, p.allocataire.id)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {nomComplet}
            </Link>
          ) : (
            "Allocataire non rattaché"
          )
        }
        pills={<StatutPropositionBadge statut={p.statut} statutLabel={p.statutLabel} />}
        actions={
          relancable ? (
            <button
              type="button"
              className={"btn btn-sm detail-proposition__relancer " + (p.blocage ? "btn-warning" : "btn-default")}
              disabled={enCoursDeRelance}
              onClick={() => onRelancer(p.idConsequenceSanction)}
              title={p.blocage ? "Réessayer après avoir corrigé ce qui bloquait" : "Redemander le traitement sans attendre le passage automatique"}
            >
              <i className={"fa mr-1 " + (enCoursDeRelance ? "fa-spinner fa-spin" : "fa-redo")} aria-hidden="true" />
              Relancer
            </button>
          ) : null
        }
      >
        <FactList
          facts={[
            { id: "nft", label: "N° France Travail", value: p.numeroFranceTravail, mono: true },
            { id: "recu", label: "Reçue le", value: p.dateReception ? formatDateHeure(p.dateReception) : null },
          ].filter((f) => f.value)}
        />
      </FicheHeader>

      {p.blocage && (
        <div className="alert alert-danger mb-0" role="alert">
          <strong>
            <i className="fa fa-exclamation-triangle mr-1" aria-hidden="true" />
            Dernière tentative en échec
          </strong>
          <div>{p.blocage.raison}</div>
          <small className="text-muted">
            À l'étape « {p.blocage.etapeLabel} », depuis le {formatDateHeure(p.blocage.depuis)}
          </small>
        </div>
      )}

      <FicheSection id="sanction" title="Sanction">
        <div className="sanction-compare">
          <div className="sanction-card sanction-card--proposee">
            <div className="sanction-card__eyebrow">Proposée par France Travail</div>
            <div className="sanction-card__label">{p.propositionFT?.label || "—"}</div>
            {p.familleManquementLabel && (
              <div className="sanction-card__meta">{p.familleManquementLabel}</div>
            )}
          </div>

          <div className={`sanction-card sanction-card--finale sanction-card--${finale.ton}`}>
            <div className="sanction-card__eyebrow">{finale.titre}</div>
            <div className="sanction-card__label">{finale.label}</div>
            {(finale.mention || i?.dateEP || i?.decisionDirecteurLabel) && (
              <div className="sanction-card__meta">
                {[
                  finale.mention,
                  i?.dateEP ? `EP du ${tools.formatDate(i.dateEP)}${i.epFinalisee ? "" : " (en cours)"}` : null,
                  i?.decisionDirecteurLabel ? `Directeur : ${i.decisionDirecteurLabel}` : null,
                ].filter(Boolean).join(" · ")}
              </div>
            )}
            {p.statut === "REJETEE" && p.detailRejet && (
              <div className="sanction-card__meta">{p.detailRejet}</div>
            )}
            {(i?.radiationBRSA || i?.radiationDE) && (
              <div className="sanction-card__badges">
                {i.radiationBRSA && (
                  <span className="sanction-tag sanction-tag--ko" title="Radiation de la liste des BRSA, sauf si mise en conformité">
                    <i className="fa fa-user-times" aria-hidden="true" /> Radiation BRSA
                  </span>
                )}
                {i.radiationDE && (
                  <span className="sanction-tag sanction-tag--ko" title="Radiation de la liste des demandeurs d'emploi">
                    <i className="fa fa-briefcase" aria-hidden="true" /> Radiation DE
                  </span>
                )}
              </div>
            )}
            {i?.commentaireInterne && (
              <blockquote className="sanction-card__quote">{i.commentaireInterne}</blockquote>
            )}
          </div>
        </div>
      </FicheSection>

      <FicheSection id="proposition-ft" title="Proposition France Travail">
        {p.familleManquementLabel && (
          <div className="detail-proposition__manquement">{p.familleManquementLabel}</div>
        )}
        <dl className="detail-proposition__facts">
          <Fait label="Sanction proposée">{p.propositionFT?.label}</Fait>
          <Fait label="Manquement le">{d?.dateManquement ? tools.formatDate(d.dateManquement) : null}</Fait>
          <Fait label="Avertissement le">{d?.dateAvertissement ? tools.formatDate(d.dateAvertissement) : null}</Fait>
          <Fait label="Indices">
            {d?.faisceauIndices?.length ? (
              <ul>
                {d.faisceauIndices.map((ind, k) => (
                  <li key={k}>
                    {ind.natureLabel}
                    {ind.dateConstat ? ` — constaté le ${tools.formatDate(ind.dateConstat)}` : ""}
                  </li>
                ))}
              </ul>
            ) : null}
          </Fait>
          <Fait label="Remobilisation">{d?.demarcheRemobilisation}</Fait>
        </dl>
        {d?.justification && (
          <blockquote className="detail-proposition__justification">
            {d.justification}
            <footer>Justification du conseiller France Travail</footer>
          </blockquote>
        )}
        {(d?.identifiantManquement || d?.codeInitiateur) && (
          <div className="detail-proposition__meta">
            {[
              d.identifiantManquement ? `Manquement FT n° ${d.identifiantManquement}` : null,
              d.codeInitiateur ? `initié par ${d.codeInitiateur}` : null,
              d.delegation ? "délégation" : null,
            ].filter(Boolean).join(" · ")}
          </div>
        )}
        {p.detailLevee && (
          <div className="detail-proposition__levee">
            <div className="detail-proposition__manquement">Demande de levée de suspension</div>
            <dl className="detail-proposition__facts">
              <Fait label="Demandée le">{p.detailLevee.dateDemandeLevee ? tools.formatDate(p.detailLevee.dateDemandeLevee) : null}</Fait>
              <Fait label="Sanction visée">{p.detailLevee.sanctionOrigineLabel}</Fait>
              <Fait label="Justificatif">{p.detailLevee.justificatif}</Fait>
              <Fait label="Non recevable">{p.detailLevee.nonRecevable}</Fait>
            </dl>
          </div>
        )}
      </FicheSection>

      {aUneReponse && (
        <FicheSection id="reponse-ft" title="Réponse à France Travail">
          <ul className="reponse-ft">
            {p.statut === "REJETEE" && p.codeSortieRefus && (
              <LigneReponse ton="ko" icon="ban" code={p.codeSortieRefus}>
                Refus automatique transmis{p.motifRejetLabel ? ` — ${p.motifRejetLabel}` : ""}
              </LigneReponse>
            )}
            {p.decisionRemontee && (
              <LigneReponse
                ton={p.decisionRemontee.priseEnCompte ? "ok" : "warn"}
                icon={p.decisionRemontee.priseEnCompte ? "check-circle" : "exclamation-circle"}
                code={p.decisionRemontee.codeSortie}
              >
                <strong>{p.decisionRemontee.label}</strong>
                {p.decisionRemontee.priseEnCompte ? " reçue par France Travail" : " non prise en compte par France Travail"}
                {p.decisionRemontee.dateRemontee ? ` le ${tools.formatDate(p.decisionRemontee.dateRemontee)}` : ""}
              </LigneReponse>
            )}
            {p.detailLevee?.reponse && (
              <LigneReponse
                ton={p.detailLevee.reponse.codeSortie === "C001" ? "ok" : "warn"}
                icon={p.detailLevee.reponse.typeEvenement === "VALID_LEVEE_SUSP" ? "unlock-alt" : "lock"}
                code={p.detailLevee.reponse.codeSortie}
                className="reponse-ft__row--levee"
              >
                {/* Une levée non recevable est refusée par la Caf, sans décision du Directeur. */}
                <strong>Réponse à la levée</strong> : {p.detailLevee.reponse.label}
                {p.detailLevee.nonRecevable ? " (suspension terminée)" : " par le Directeur de la Caf"}
                {p.detailLevee.reponse.dateReponse ? `, transmise le ${tools.formatDate(p.detailLevee.reponse.dateReponse)}` : ""}
              </LigneReponse>
            )}
            {p.radiation && (
              <LigneReponse
                ton="ko"
                icon="user-times"
                code={p.radiation.codeSortie}
                className="reponse-ft__row--radiation"
              >
                <strong className="reponse-ft__radiation">Radiation DE</strong> : {p.radiation.label}
                {p.radiation.dateDemande ? `, demandée le ${tools.formatDate(p.radiation.dateDemande)}` : ""}
              </LigneReponse>
            )}
          </ul>
        </FicheSection>
      )}

      <FicheSection id="chronologie" title="Chronologie">
        <Timeline items={chronologie(p)} emptyMessage="Aucun événement." ariaLabel="Chronologie de la proposition" />
      </FicheSection>
    </div>
  );
};

export default DetailPropositionSanctionFT;
